/**
 * WishlistManager – Showtime V2
 * Native wishlist using localStorage with event-driven UI updates.
 * Follows the RecentlyViewedTracker pattern from product.js.
 *
 * Phase 1: localStorage for all users (guest + logged-in).
 * Phase 2 (future): Storefront API customer metafield sync for logged-in users.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'castleJewelry_wishlist';
  var MAX_ITEMS = 100;

  /* ── Heart SVG strings for JS-rendered cards ── */
  var HEART_OUTLINE = '<svg class="icon icon-heart" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>';
  var HEART_FILLED = '<svg class="icon icon-heart-filled" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>';

  /* ── HTML escape utility ── */
  var _escDiv = document.createElement('div');
  function esc(str) {
    _escDiv.textContent = str || '';
    return _escDiv.innerHTML;
  }

  /* ── Constructor ── */
  function WishlistManager() {
    this.items = this._load();
    this._bindEvents();
    this._initButtons();
    this.updateHeaderCount();

    /* Dispatch loaded event */
    document.dispatchEvent(new CustomEvent('wishlist:loaded', {
      detail: { count: this.items.length }
    }));
  }

  /* ── localStorage Persistence ── */

  WishlistManager.prototype._load = function() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch(e) {
      return [];
    }
  };

  WishlistManager.prototype._save = function() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch(e) { /* localStorage full or unavailable */ }
  };

  /* ── Core CRUD ── */

  WishlistManager.prototype.add = function(handle, id, title) {
    /* Remove if already exists (dedup), then prepend */
    this.items = this.items.filter(function(item) { return item.handle !== handle; });
    this.items.unshift({ handle: handle, id: id, title: title, timestamp: Date.now() });
    if (this.items.length > MAX_ITEMS) this.items = this.items.slice(0, MAX_ITEMS);
    this._save();
    this._dispatch('add', handle);
    this._announce('Added to wishlist');
  };

  WishlistManager.prototype.remove = function(handle) {
    this.items = this.items.filter(function(item) { return item.handle !== handle; });
    this._save();
    this._dispatch('remove', handle);
    this._announce('Removed from wishlist');
  };

  WishlistManager.prototype.toggle = function(handle, id, title) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    } else {
      this.add(handle, id, title);
      return true;
    }
  };

  WishlistManager.prototype.has = function(handle) {
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].handle === handle) return true;
    }
    return false;
  };

  WishlistManager.prototype.getAll = function() {
    return this.items.slice();
  };

  WishlistManager.prototype.getCount = function() {
    return this.items.length;
  };

  WishlistManager.prototype.clear = function() {
    this.items = [];
    this._save();
    this._dispatch('clear', null);
  };

  /* ── Event Dispatch ── */

  WishlistManager.prototype._dispatch = function(action, handle) {
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { action: action, handle: handle, count: this.items.length }
    }));
    this.updateAllButtons();
    this.updateHeaderCount();
  };

  /* ── Screen Reader Announcements ── */

  WishlistManager.prototype._announce = function(message) {
    var region = document.getElementById('aria-live-region');
    if (region) {
      region.textContent = message;
    }
  };

  /* ── UI: Update All Heart Buttons ── */

  WishlistManager.prototype.updateAllButtons = function() {
    var self = this;
    var buttons = document.querySelectorAll('[data-wishlist-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var handle = btn.getAttribute('data-product-handle');
      var isActive = self.has(handle);
      if (isActive) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Add to wishlist');
      }
      /* Update PDP label text if present */
      var label = btn.querySelector('[data-wishlist-btn-label]');
      if (label) {
        label.textContent = isActive ? 'Saved to Wishlist' : 'Add to Wishlist';
      }
    }
  };

  /* ── UI: Initialize buttons on page load ── */

  WishlistManager.prototype._initButtons = function() {
    this.updateAllButtons();
  };

  /* ── UI: Header Count Badge ── */

  WishlistManager.prototype.updateHeaderCount = function() {
    var count = this.getCount();
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      badge.textContent = count;
      if (count > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  };

  /* ── Event Binding ── */

  WishlistManager.prototype._bindEvents = function() {
    var self = this;

    /* Delegated click handler for all wishlist buttons (Liquid + JS-rendered) */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      var handle = btn.getAttribute('data-product-handle');
      var id = parseInt(btn.getAttribute('data-product-id'), 10);
      var title = btn.getAttribute('data-product-title') || '';

      var added = self.toggle(handle, id, title);

      /* Pop animation */
      btn.classList.remove('is-animating');
      /* Force reflow to restart animation */
      void btn.offsetWidth;
      btn.classList.add('is-animating');
      setTimeout(function() { btn.classList.remove('is-animating'); }, 350);
    });

    /* Cross-tab sync: update UI when another tab changes localStorage */
    window.addEventListener('storage', function(e) {
      if (e.key !== STORAGE_KEY) return;
      self.items = self._load();
      self.updateAllButtons();
      self.updateHeaderCount();
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { action: 'sync', handle: null, count: self.items.length }
      }));
    });
  };

  /* ── Static: Build heart button HTML for JS-rendered cards ── */
  /* Attached to both constructor and prototype so it works whether
     window.WishlistManager is the class or the singleton instance. */

  function buildButtonHTML(handle, id, title) {
    var mgr = window.WishlistManager;
    var isActive = mgr && (typeof mgr.has === 'function') && mgr.has(handle);
    var activeClass = isActive ? ' is-active' : '';
    var ariaLabel = isActive ? 'Remove from wishlist' : 'Add to wishlist';
    var ariaPressed = isActive ? 'true' : 'false';

    return '<button type="button" class="wishlist-btn' + activeClass + '" '
      + 'data-wishlist-btn '
      + 'data-product-handle="' + handle + '" '
      + 'data-product-id="' + id + '" '
      + 'data-product-title="' + esc(title) + '" '
      + 'aria-label="' + ariaLabel + '" '
      + 'aria-pressed="' + ariaPressed + '">'
      + '<span class="wishlist-btn__icon wishlist-btn__icon--outline">' + HEART_OUTLINE + '</span>'
      + '<span class="wishlist-btn__icon wishlist-btn__icon--filled">' + HEART_FILLED + '</span>'
      + '</button>';
  }

  WishlistManager.buildButtonHTML = buildButtonHTML;
  WishlistManager.prototype.buildButtonHTML = buildButtonHTML;

  /* ── Initialize Singleton ── */
  window.WishlistManager = new WishlistManager();

})();
